// 齊文樓層開放公告（公開，免登入）
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { SEAT_BOOKING_URL } from '../constants/venueLinks';

axios.defaults.withCredentials = true;

const pad2 = (n) => String(n).padStart(2, '0');

const DEFAULT_SEAT_URL = SEAT_BOOKING_URL;
const DEFAULT_CALENDAR_URL = 'https://sglrt.thesingularity.tw/weekly-activity';

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDaysYmd(ymd, delta) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return todayYmd();
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + delta, 12, 0, 0, 0);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function parseYmd(ymd) {
  const m = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
}

/** 會員卡／週活動淺色：暖白、咖啡字、細邊 */
const T = {
  pageBg: 'linear-gradient(180deg, #fdfbf8 0%, #f7f0e8 40%, #f0e8de 100%)',
  text: '#3d2510',
  textSoft: '#5e3a1a',
  muted: '#8B6F47',
  accent: '#5c3d24',
  card: '#fffdfa',
  cardEdge: 'rgba(94,58,26,.14)',
  cardShadow: '0 2px 12px rgba(62, 42, 28, .06), inset 0 1px 0 rgba(255,255,255,.85)',
  open: '#5cb87a',
  closed: '#6f6560',
  now: '#c41e1e',
  tick: 'rgba(92, 61, 36, .55)',
  railBg: 'rgba(94,58,26,.07)',
  line: 'rgba(94,58,26,.1)',
  chipBorder: 'rgba(94,58,26,.2)',
  chipHi: 'linear-gradient(180deg, #8B6F47 0%, #6B4423 100%)',
};

function dayTitle(ymd) {
  const d = parseYmd(ymd);
  const w = ['日', '一', '二', '三', '四', '五', '六'][d.getDay()];
  return `${ymd.replace(/-/g, '／')} 週${w}`;
}

const legendSwatchStyle = (bg) => ({
  width: 14,
  height: 10,
  borderRadius: 3,
  background: bg,
  border: `1px solid ${T.line}`,
  flexShrink: 0,
});

function ColorLegend() {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px 16px',
        fontSize: 11,
        fontWeight: 700,
        color: T.textSoft,
        lineHeight: 1.2,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={legendSwatchStyle(T.open)} aria-hidden />
        綠色：開放
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={legendSwatchStyle(T.closed)} aria-hidden />
        灰色：關閉
      </span>
    </div>
  );
}

/** 各樓層橫軸結束時間（覆寫預設「翌日 01:00」）。3F 平日只到 23:30；週日各樓層皆 19:00。 */
const ZONE_WINDOW_END_OVERRIDE = {
  '3f': { hour: 23, minute: 30, nextDay: false },
};

function zoneWinBounds(ymd, zoneKey) {
  const d = parseYmd(ymd);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 9, 0, 0, 0);
  if (d.getDay() === 0) {
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 19, 0, 0, 0);
    return { startMs: start.getTime(), endMs: end.getTime() };
  }
  const ov = ZONE_WINDOW_END_OVERRIDE[zoneKey];
  const end = ov
    ? new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate() + (ov.nextDay ? 1 : 0),
      ov.hour,
      ov.minute,
      0,
      0,
    )
    : new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 1, 0, 0, 0);
  return { startMs: start.getTime(), endMs: end.getTime() };
}

function formatWindowEdge(ms, baseYmd) {
  const d = new Date(ms);
  const m = String(baseYmd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const base = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0).getTime();
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  if (dayStart > base) return `翌日${hm}`;
  return hm;
}

function segmentBarStyle(seg, winStartMs, winEndMs) {
  const total = Math.max(1, winEndMs - winStartMs);
  const left = ((seg.startMs - winStartMs) / total) * 100;
  const width = ((seg.endMs - seg.startMs) / total) * 100;
  return {
    left: `${Math.max(0, Math.min(100, left))}%`,
    width: `${Math.max(0, Math.min(100 - left, width))}%`,
    background: seg.state === 'open' ? T.open : T.closed,
  };
}

function formatTickLabel(ms, dayYmd) {
  const d = new Date(ms);
  const m = String(dayYmd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  const base = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0).getTime();
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
  const hm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  if (dayStart > base) return `翌${hm}`;
  return hm;
}

function pctInWindow(ms, winStartMs, winEndMs) {
  const total = Math.max(1, winEndMs - winStartMs);
  return ((ms - winStartMs) / total) * 100;
}

/** 色條僅顯示營業窗內（週日 09:00～19:00；平日至翌日 01:00） */
function clipSegmentsForWindow(segments, winStartMs, winEndMs) {
  if (!Array.isArray(segments)) return [];
  const out = [];
  for (const seg of segments) {
    const s = Math.max(seg.startMs, winStartMs);
    const e = Math.min(seg.endMs, winEndMs);
    if (s >= e) continue;
    out.push({ ...seg, startMs: s, endMs: e });
  }
  return out;
}

function internalBoundaryMs(segments, winStartMs, winEndMs) {
  const set = new Set();
  (segments || []).forEach((seg) => {
    [seg.startMs, seg.endMs].forEach((t) => {
      if (t > winStartMs && t < winEndMs) set.add(t);
    });
  });
  return [...set].sort((a, b) => a - b);
}

const BAR_H = 13;
const LABEL_H = 18;

function ZoneCard({ z, winBounds, isToday, dateYmd }) {
  const { startMs, endMs } = winBounds;

  const nowPct = useMemo(() => {
    if (!isToday) return null;
    const t = Date.now();
    if (t < startMs || t >= endMs) return null;
    return pctInWindow(t, startMs, endMs);
  }, [isToday, startMs, endMs]);

  const startLabel = useMemo(() => formatWindowEdge(startMs, dateYmd), [startMs, dateYmd]);
  const endLabel = useMemo(() => formatWindowEdge(endMs, dateYmd), [endMs, dateYmd]);

  const displaySegments = useMemo(
    () => clipSegmentsForWindow(z.segments, startMs, endMs),
    [z.segments, startMs, endMs],
  );

  const boundaries = useMemo(
    () => internalBoundaryMs(displaySegments, startMs, endMs),
    [displaySegments, startMs, endMs],
  );

  const openLine = useMemo(() => {
    const arr = Array.isArray(z.summaryLines) ? z.summaryLines : [];
    const joined = arr.filter((x) => x && x !== '—').join('、');
    return joined || '—';
  }, [z.summaryLines]);

  return (
    <article
      style={{
        background: T.card,
        border: `1px solid ${T.cardEdge}`,
        borderRadius: 12,
        padding: '10px 12px 11px',
        boxShadow: T.cardShadow,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minHeight: 0,
      }}
    >
      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: T.accent, letterSpacing: 0.02, lineHeight: 1.2 }}>
        {z.label}
      </h2>

      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, lineHeight: 1.28 }}>{openLine}</div>

      {/* 刻度與色條共用同一寬度座標（單一 position 參考層） */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          boxSizing: 'border-box',
          marginTop: isToday && nowPct != null ? 18 : 2,
        }}
      >
        <div
          style={{
            position: 'relative',
            height: BAR_H,
            borderRadius: 7,
            background: T.railBg,
            overflow: 'visible',
            border: `1px solid ${T.line}`,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ position: 'absolute', inset: 0, borderRadius: 6, overflow: 'hidden' }}>
            {displaySegments.map((seg, i) => (
              <div
                key={i}
                title={`${seg.startHm}–${seg.endHm}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  height: '100%',
                  borderRadius: 3,
                  ...segmentBarStyle(seg, startMs, endMs),
                  opacity: 0.96,
                }}
              />
            ))}
          </div>
          {boundaries.map((ms) => {
            const p = pctInWindow(ms, startMs, endMs);
            return (
              <div
                key={ms}
                style={{
                  position: 'absolute',
                  left: `${p}%`,
                  top: 0,
                  height: BAR_H,
                  width: 2,
                  marginLeft: -1,
                  background: T.tick,
                  borderRadius: 1,
                  zIndex: 3,
                  pointerEvents: 'none',
                }}
              />
            );
          })}
          {nowPct != null && (
            <>
              <div
                title="現在"
                style={{
                  position: 'absolute',
                  left: `${nowPct}%`,
                  top: -2,
                  height: BAR_H + 4,
                  width: 2,
                  marginLeft: -1,
                  background: T.now,
                  borderRadius: 1,
                  zIndex: 4,
                  boxShadow: '0 0 0 1px rgba(255,255,255,.95)',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: `${nowPct}%`,
                  top: -13,
                  transform: 'translateX(-50%)',
                  fontSize: 9,
                  fontWeight: 800,
                  color: '#fff',
                  background: T.now,
                  padding: '1px 4px',
                  borderRadius: 4,
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                  zIndex: 5,
                  pointerEvents: 'none',
                  boxShadow: '0 1px 2px rgba(0,0,0,.18)',
                }}
              >
                現在
              </div>
            </>
          )}
        </div>

        <div
          style={{
            position: 'relative',
            height: LABEL_H,
            marginTop: 1,
            width: '100%',
          }}
        >
          {boundaries.map((ms) => {
            const p = pctInWindow(ms, startMs, endMs);
            return (
              <div
                key={`l-${ms}`}
                style={{
                  position: 'absolute',
                  left: `${p}%`,
                  top: 0,
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                }}
              >
                <div style={{ fontSize: 9, fontWeight: 800, color: T.text, lineHeight: 1.1 }}>
                  {formatTickLabel(ms, dateYmd)}
                </div>
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 9,
            fontWeight: 700,
            color: T.muted,
            marginTop: 1,
            lineHeight: 1.1,
          }}
        >
          <span>{startLabel}</span>
          <span>{endLabel}</span>
        </div>
      </div>

      {Array.isArray(z.activities) && z.activities.length > 0 && (
        <div style={{ borderTop: `1px solid ${T.line}`, paddingTop: 6, marginTop: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.muted, marginBottom: 3, lineHeight: 1.1 }}>活動</div>
          <ul style={{ margin: 0, paddingLeft: 14, fontSize: 12, lineHeight: 1.28, color: T.textSoft }}>
            {z.activities.map((a, i) => (
              <li key={i} style={{ marginBottom: 1 }}>
                {a.time || '—'} {a.title}
              </li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

function readHideQuickLinks(searchParams) {
  const g = (k) => String(searchParams.get(k) ?? '').trim().toLowerCase();
  const truthy = (s) => ['1', 'true', 'yes', 'on'].includes(s);
  const falsy = (s) => ['0', 'false', 'no', 'off'].includes(s);
  if (truthy(g('embed')) || truthy(g('iframe'))) return true;
  if (truthy(g('hidelinks')) || truthy(g('hidetoplinks')) || truthy(g('hidenav'))) return true;
  if (falsy(g('toplinks')) || falsy(g('buttons')) || falsy(g('links'))) return true;
  return false;
}

export default function FloorOpenNotice() {
  const [searchParams] = useSearchParams();
  const hideQuickLinks = useMemo(() => readHideQuickLinks(searchParams), [searchParams]);

  const seatUrl = searchParams.get('seatUrl')?.trim() || DEFAULT_SEAT_URL;
  const calendarUrl = searchParams.get('calendarUrl')?.trim() || DEFAULT_CALENDAR_URL;

  const t0 = useMemo(() => todayYmd(), []);
  const maxYmd = useMemo(() => addDaysYmd(t0, 6), [t0]);
  const [date, setDate] = useState(t0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const allowed = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i += 1) out.push(addDaysYmd(t0, i));
    return out;
  }, [t0]);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const { data: d } = await axios.get('/api/public/floor-open', { params: { date } });
      if (!d?.success) throw new Error(d?.error || 'load_failed');
      setData(d);
    } catch (e) {
      setData(null);
      setErr(e?.response?.data?.message || e?.response?.data?.error || e?.message || '載入失敗');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const isToday = date === t0;
  const zones = data?.zones || [];

  const chipHi = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '5px 10px',
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 800,
    color: '#fff',
    textDecoration: 'none',
    background: T.chipHi,
    border: '1px solid rgba(0,0,0,.08)',
    boxShadow: '0 1px 3px rgba(62,42,28,.12)',
    lineHeight: 1.2,
  };

  const dateInputStyle = {
    padding: '5px 8px',
    fontSize: 12,
    borderRadius: 8,
    border: `1px solid ${T.chipBorder}`,
    color: T.text,
    background: '#fff',
    fontWeight: 700,
    lineHeight: 1.2,
    maxWidth: 132,
  };

  const canPrev = date > t0;
  const canNext = date < maxYmd;
  const arrowBtnStyle = (enabled) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    padding: 0,
    borderRadius: 8,
    border: `1px solid ${T.chipBorder}`,
    background: enabled ? '#fff' : 'rgba(94,58,26,.05)',
    color: enabled ? T.accent : 'rgba(94,58,26,.35)',
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1,
    cursor: enabled ? 'pointer' : 'not-allowed',
    boxShadow: enabled ? '0 1px 2px rgba(62,42,28,.06)' : 'none',
    transition: 'background .12s ease, color .12s ease',
  });

  const goPrev = () => {
    if (!canPrev) return;
    setDate(addDaysYmd(date, -1));
  };
  const goNext = () => {
    if (!canNext) return;
    setDate(addDaysYmd(date, 1));
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.pageBg,
        color: T.text,
        padding: '12px 12px 28px',
        boxSizing: 'border-box',
        fontFamily: '"Noto Sans TC", "PingFang TC", "Microsoft JhengHei", sans-serif',
      }}
    >
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <header
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            marginBottom: 12,
            padding: '12px 14px',
            background: T.card,
            border: `1px solid ${T.cardEdge}`,
            borderRadius: 12,
            boxShadow: T.cardShadow,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <h1
              style={{
                fontSize: 'clamp(1.05rem, 3.5vw, 1.35rem)',
                fontWeight: 800,
                letterSpacing: 0.03,
                margin: 0,
                color: T.accent,
                lineHeight: 1.2,
              }}
            >
              齊文樓層開放公告
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: T.muted, fontWeight: 600, lineHeight: 1.25 }}>
              {dayTitle(date)}
            </p>
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <button
              type="button"
              onClick={goPrev}
              disabled={!canPrev}
              aria-label="前一天"
              style={arrowBtnStyle(canPrev)}
            >
              ‹
            </button>
            <input
              type="date"
              value={date}
              min={t0}
              max={maxYmd}
              onChange={(e) => {
                const v = e.target.value;
                if (allowed.includes(v)) setDate(v);
              }}
              style={dateInputStyle}
            />
            <button
              type="button"
              onClick={goNext}
              disabled={!canNext}
              aria-label="後一天"
              style={arrowBtnStyle(canNext)}
            >
              ›
            </button>
            {!hideQuickLinks && (
              <>
                <a href={seatUrl} target="_blank" rel="noopener noreferrer" style={chipHi}>
                  座位預約
                </a>
                <a href={calendarUrl} target="_blank" rel="noopener noreferrer" style={chipHi}>
                  活動月曆
                </a>
              </>
            )}
          </div>

          <ColorLegend />
        </header>

        {loading && <p style={{ textAlign: 'center', color: T.muted, fontSize: 13, margin: '6px 0' }}>載入中…</p>}
        {err && !loading && (
          <p style={{ textAlign: 'center', color: '#b91c1c', fontSize: 13, margin: '6px 0' }}>{err}</p>
        )}

        {!loading && zones.length === 4 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              alignItems: 'stretch',
            }}
          >
            <div style={{ gridColumn: '1 / -1' }}>
              <ZoneCard
                z={zones[0]}
                winBounds={zoneWinBounds(date, zones[0].zone_key)}
                isToday={isToday}
                dateYmd={date}
              />
            </div>
            <ZoneCard
              z={zones[1]}
              winBounds={zoneWinBounds(date, zones[1].zone_key)}
              isToday={isToday}
              dateYmd={date}
            />
            <ZoneCard
              z={zones[2]}
              winBounds={zoneWinBounds(date, zones[2].zone_key)}
              isToday={isToday}
              dateYmd={date}
            />
            <div style={{ gridColumn: '1 / -1' }}>
              <ZoneCard
                z={zones[3]}
                winBounds={zoneWinBounds(date, zones[3].zone_key)}
                isToday={isToday}
                dateYmd={date}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
